// The /download page — "Get Waytide". A receipt sheet in the landing/sign-in
// idiom: the masthead brand cluster (wordmark + human tag + the mono rev
// telemetry) with the tactical [ DOWNLOAD ] tag, a macro title, then the shared
// PlatformDownloads block (the primary download → every desktop → first run →
// phone). The desktop installer is the core input — it runs beside the agent
// and feeds everything else. Square / flat / one amber — design-gate canon.

import { PlatformDownloads } from "./PlatformDownloads";
import {
  CornerTicks,
  Eyebrow,
  ReceiptPage,
  TearRule,
  Wordmark,
} from "../receipt/ReceiptKit";
import {
  DEFAULT_INSTALLERS,
  readUserAgent,
  selectPreferredInstaller,
  type DesktopInstallerOption,
} from "./installers";
import { APP_VERSION } from "./buildIdentity";
import "./download.css";

// Re-exported for the existing test + any external importer.
export { selectPreferredInstaller };
export type { DesktopInstallerOption };

type DesktopDownloadPageProps = {
  installers?: readonly DesktopInstallerOption[];
  userAgent?: string;
};

export function DesktopDownloadPage({
  installers = DEFAULT_INSTALLERS,
  userAgent = readUserAgent(),
}: DesktopDownloadPageProps = {}) {
  return (
    <ReceiptPage className="wt-download">
      <main className="wt-download__sheet">
        <CornerTicks />

        {/* The masthead — brand cluster left, the tactical tag right. The rev
            line is the machine record (mono, dim, uppercased by CSS): version +
            channel in one truthful readout. */}
        <header className="wt-download__mast">
          <div className="wt-download__brand">
            <Wordmark />
            <span className="wt-download__tag">agent supervision · desktop</span>
            <span className="wt-download__rev" data-testid="brand-version">
              <span className="wt-download__live" aria-hidden>
                ●
              </span>
              rev v{APP_VERSION} · pre-release
            </span>
          </div>
          <Eyebrow label="DOWNLOAD" />
        </header>

        <TearRule />

        {/* The head — product name (macro, uppercase via CSS) + the one-liner. */}
        <div className="wt-download__head">
          <h1 className="wt-download__title">Get Waytide</h1>
          <p className="wt-download__lede">
            Install the desktop app — it runs beside your coding agent and is
            the input everything else builds on.
          </p>
        </div>

        <PlatformDownloads installers={installers} userAgent={userAgent} />

        <TearRule />

        {/* The foot — the version/channel record. Builds are unsigned during
            the testing phase; the first-run block above clears the warnings. */}
        <p className="wt-download__foot">
          waytide desktop · v{APP_VERSION} · pre-release channel · unsigned
          preview builds
        </p>
      </main>
    </ReceiptPage>
  );
}
